import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  Channel,
  channelApiClient,
  ChannelCreateErrorResponse,
  ChannelForm,
  ChannelUpdateErrorResponse,
} from '@src/api/shared/community/channelApiClient';
import CreateEditChannelModal from '@src/components/CreateEditChannelModal.tsx';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import CommunityChannelHeader from '@src/features/shared/community/components/CommunityChannelHeader';
import SortableChannelItem from '@src/features/shared/community/components/SortableChannelItem';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { SHARED_URL } from '@src/routes/shared';
import {
  addChannel,
  removeChannel,
  updateChannel,
  updateChannelOrders,
} from '@src/store/channelSlice.ts';
import { AppDispatch } from '@src/store/store';
import Button from '@src/ui/elements/button/Button';
import Title from '@src/ui/elements/typography/Title.tsx';
import logger from '@src/utils/logger';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import { ConfirmDeleteModal } from './components/channel/ConfirmDeleteModal';

const CommunityChannelListForm: React.FC = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createErrors, setCreateErrors] = useState<ChannelCreateErrorResponse | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [updateErrors, setUpdateErrors] = useState<ChannelUpdateErrorResponse | undefined>();
  const navigate = useNavigate();
  const toast = useToast();
  const { toggle: toggleMobileSidebar } = useContext(MobileSidebarContext);
  const dispatch = useDispatch<AppDispatch>();

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const tableColumnStyles = {
    drag: 'w-[42px]',
    order: 'w-[80px] md:w-[80px]',
    name: 'flex-1 min-w-[140px]',
    visibility: 'w-[140px] md:w-0 md:hidden',
    permission: 'w-[140px] md:w-0 md:hidden',
    fileAttachment: 'w-[140px] md:w-0 md:hidden',
    emailNotification: 'w-[140px] md:w-0 md:hidden',
    action: 'w-[54px] md:w-[66px]',
  };

  const loadChannels = async () => {
    const res = await channelApiClient.index({ all: true });
    handleApiResponse(
      res,
      (data) => {
        setChannels(data);
      },
      undefined,
      () => {
        toast(TOAST_MESSAGE.LOAD_FAILED);
      },
    );
  };

  const handleMembersClick = (channel: Channel) => () => {
    navigate(generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code: channel.code }));
  };

  const handleEditClick = (channel: Channel) => () => {
    setEditingChannel(channel);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (channel: Channel) => async () => {
    setEditingChannel(channel);
    setIsDeleteModalOpen(true);
  };

  const onDelete = (channel: Channel) => async () => {
    const res = await channelApiClient.delete(channel.code);
    handleApiResponse(res, () => {
      loadChannels();
      dispatch(removeChannel(channel.code));
      toast(TOAST_MESSAGE.DELETE);
      setIsDeleteModalOpen(false);
    });
  };

  const handleCreateSave = async (formData: ChannelForm) => {
    const res = await channelApiClient.create({ channel: formData });
    handleApiResponse(
      res,
      (data) => {
        handleCreateClose();
        loadChannels();
        dispatch(addChannel(data));
        toast(TOAST_MESSAGE.REGISTER);
      },
      (error) => {
        setCreateErrors(error);
      },
    );
  };

  const handleEditSave = async (formData: ChannelForm) => {
    if (!editingChannel) {
      return;
    }

    const res = await channelApiClient.update(editingChannel.code, { channel: formData });
    handleApiResponse(
      res,
      (data) => {
        handleEditClose();
        loadChannels();
        dispatch(updateChannel(data));
        toast(TOAST_MESSAGE.UPDATE);
      },
      (error) => {
        setUpdateErrors(error);
      },
    );
  };

  const handleCreateClose = () => {
    setIsCreateModalOpen(false);
    setCreateErrors(undefined);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
    setEditingChannel(null);
    setUpdateErrors(undefined);
  };

  const handleDeleteClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = channels.findIndex((channel) => channel.code === active.id);
    const newIndex = channels.findIndex((channel) => channel.code === over.id);

    const newChannels = arrayMove(channels, oldIndex, newIndex);
    setChannels(newChannels);

    const ordersData = {
      orders: newChannels.map((channel, index) => ({
        code: channel.code,
        order: index + 1,
      })),
    };

    const res = await channelApiClient.updateOrder(ordersData);
    handleApiResponse(
      res,
      () => {
        dispatch(updateChannelOrders(ordersData));
      },
      (error) => {
        logger.error('チャンネルの順番を更新できませんでした', error);
      },
    );
  };

  useEffect(() => {
    loadChannels();
  }, []);

  return (
    <>
      <CommunityChannelHeader
        channel={null}
        openSettingMenu={false}
        menuRef={{ current: null }}
        isTenantAdmin={false}
        isLoggedIn={false}
        code={undefined}
        toggleMobileSidebar={toggleMobileSidebar}
        handleSettingMenuClick={() => {}}
        handleChannelSettingOpenButtonClick={() => {}}
        handleLinkClick={() => {}}
        navigate={navigate}
        showDisplayNameBanner={false}
        isSticky={false}
        containerClassName='hidden lg:flex'
        innerContainerClassName='h-[60px]'
      />
      <main className='px-6 py-11 md:px-2 md:pb-8'>
        <div className='mb-6'>
          <div className='mb-4'>
            <Title>チャンネル一覧</Title>
          </div>
          <div className='flex justify-end'>
            <Button
              size='s'
              variant='primary'
              onClick={() => setIsCreateModalOpen(true)}
            >
              新規作成
            </Button>
          </div>
        </div>

        <div className='mb-4'>
          <div className='sticky top-0 z-10 flex bg-gray-50'>
            <div className={tableColumnStyles.drag}></div>
            <div
              className={`${tableColumnStyles.order} px-3 py-[13px] text-left text-xs font-semibold`}
            >
              表示順序
            </div>
            <div
              className={`${tableColumnStyles.name} px-3 py-[13px] text-left text-xs font-semibold`}
            >
              チャンネル名
            </div>
            <div
              className={`${tableColumnStyles.visibility} px-3 py-[13px] text-left text-xs font-semibold`}
            >
              公開設定
            </div>
            <div
              className={`${tableColumnStyles.permission} px-3 py-[13px] text-left text-xs font-semibold`}
            >
              ユーザー投稿
            </div>
            <div
              className={`${tableColumnStyles.fileAttachment} px-3 py-[13px] text-left text-xs font-semibold`}
            >
              ファイル添付
            </div>
            <div
              className={`${tableColumnStyles.emailNotification} px-3 py-[13px] text-left text-xs font-semibold`}
            >
              メール通知
            </div>
            <div className={tableColumnStyles.action}></div>
          </div>

          <div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={channels.map((c) => c.code)}>
                {channels.map((channel, index) => (
                  <SortableChannelItem
                    key={channel.code}
                    channel={channel}
                    onMembersClick={handleMembersClick(channel)}
                    onEditClick={handleEditClick(channel)}
                    onDeleteClick={handleDeleteClick(channel)}
                    tableColumnStyles={tableColumnStyles}
                    order={index + 1}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {isEditModalOpen && editingChannel && (
          <CreateEditChannelModal
            isOpen={isEditModalOpen}
            channel={editingChannel}
            errors={updateErrors}
            onSave={handleEditSave}
            onClose={handleEditClose}
          />
        )}
        <CreateEditChannelModal
          isOpen={isCreateModalOpen}
          errors={createErrors}
          onSave={handleCreateSave}
          onClose={handleCreateClose}
        />
        {isDeleteModalOpen && (
          <ConfirmDeleteModal
            onConfirm={onDelete(editingChannel!)}
            onClose={handleDeleteClose}
            name={editingChannel?.name}
          />
        )}
      </main>
    </>
  );
};

export default CommunityChannelListForm;
